/**
 * Thin facade over the signed-in user's permission payload.
 * It keeps permission lookups readable inside components and guards while delegating
 * the actual screen-access rules to the centralized RBAC config.
 */
import { Injectable, computed, inject } from '@angular/core';

import { AuthService } from '../../features/login/auth.service';
import { AccessKey, canSatisfyAccessRequirement } from './access-control.config';
import { PermissionCode } from './permission-codes';

/**
 * SERVICE: Permissions
 * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
 * Output: Returns the documented value or updates component/service state without changing the public contract.
 * Processing: Documents the Angular declaration that connects this class to templates, dependency injection, or template transforms.
 */
@Injectable({ providedIn: 'root' })
export class PermissionsService {
  private readonly authService = inject(AuthService);

  // Derived directly from the live auth profile so permission changes propagate consistently.
  readonly grantedPermissions = computed(() => this.authService.currentUser()?.permissions ?? []);
  /** STATE OWNER: currentUserRole documents the computed state boundary used by this file. */
  readonly currentUserRole = computed(() => this.authService.currentUser()?.role);

  /**
   * Returns whether the current user holds one specific backend permission code.
   */
  hasPermission(permission: PermissionCode): boolean {
    return this.grantedPermissions().includes(permission);
  }

  /**
   * BUSINESS LOGIC: hasAnyPermission
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  hasAnyPermission(permissions: readonly PermissionCode[]): boolean {
    const granted = this.grantedPermissions();
    return permissions.some((permission) => granted.includes(permission));
  }

  /**
   * BUSINESS LOGIC: hasAllPermissions
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  hasAllPermissions(permissions: readonly PermissionCode[]): boolean {
    const granted = this.grantedPermissions();
    return permissions.every((permission) => granted.includes(permission));
  }

  /**
   * Resolves one screen or module access key through the shared RBAC matrix.
   */
  canAccess(accessKey: AccessKey): boolean {
    return canSatisfyAccessRequirement(this.grantedPermissions(), this.currentUserRole(), accessKey);
  }
}
